/**
 * Pipeline de chargement de la documentation.
 *
 * Tous les fichiers `.md` sous `src/docs/**` sont importés à la build via
 * `import.meta.glob` (Vite), parsés via `gray-matter` et exposés en mémoire
 * sous forme d'arborescence `sections → pages`.
 *
 * Convention d'arborescence :
 *   src/docs/
 *     <section>/
 *       _meta.ts?             (métadonnées de la section, facultatif)
 *       index.md              (page d'accueil de la section)
 *       <slug>.md             (pages enfants)
 */

export interface DocFrontmatter {
    title: string;
    description?: string;
    section: string;
    order?: number;
    status?: 'stable' | 'beta' | 'coming';
    updated?: string;
    tags?: string[];
    hidden?: boolean;
}

export interface DocPage {
    slug: string;           // ex. "quickstart"
    section: string;        // ex. "getting-started"
    path: string;           // ex. "/docs/getting-started/quickstart"
    content: string;        // corps Markdown (sans frontmatter)
    frontmatter: DocFrontmatter;
}

export interface DocSection {
    id: string;             // slug du dossier
    label: string;
    description?: string;
    icon?: string;
    order: number;
    pages: DocPage[];
    indexPath: string;      // chemin vers la page d'ouverture
}

/* ================================================================
   Méta-données des sections (ordre + libellés affichés dans la sidebar)
   ================================================================ */
interface SectionMeta {
    label: string;
    description: string;
    icon: string;
    order: number;
}

const SECTION_META: Record<string, SectionMeta> = {
    'getting-started': {
        label: 'Premiers pas',
        description: 'Comprendre Quotio et publier son premier générateur.',
        icon: 'book',
        order: 1,
    },
    editor: {
        label: "L'éditeur",
        description: 'Branding, étapes, champs, visibilité, tarification, leads.',
        icon: 'settings',
        order: 2,
    },
    publish: {
        label: 'Publication',
        description: "Rendre son générateur accessible à vos prospects.",
        icon: 'globe',
        order: 3,
    },
    embed: {
        label: 'Intégration',
        description: "Embarquer Quotio sur votre site (iframe, widget JS, CMS).",
        icon: 'code',
        order: 4,
    },
    leads: {
        label: 'Demandes',
        description: 'Recevoir, qualifier et relancer les leads captés.',
        icon: 'users',
        order: 5,
    },
    organization: {
        label: 'Organisation',
        description: 'Changer, créer, inviter des membres dans une organisation.',
        icon: 'briefcase',
        order: 6,
    },
    account: {
        label: 'Mon compte',
        description: 'Profil, sécurité, préférences, droits Loi 25 / RGPD.',
        icon: 'user',
        order: 7,
    },
    api: {
        label: 'API & webhooks',
        description: 'Référence technique pour les intégrateurs.',
        icon: 'code',
        order: 8,
    },
    security: {
        label: 'Sécurité & conformité',
        description: 'Architecture, sous-traitants, Loi 25 et RGPD.',
        icon: 'shield',
        order: 9,
    },
    resources: {
        label: 'Ressources',
        description: 'Changelog, roadmap, FAQ et contact.',
        icon: 'layers',
        order: 10,
    },
};

/* ================================================================
   Chargement des fichiers .md via import.meta.glob
   ================================================================ */
// Vite transforme tous les .md en chaînes brutes au build (eager = inline).
const RAW_MODULES = import.meta.glob('../docs/**/*.md', {
    eager: true,
    query: '?raw',
    import: 'default',
}) as Record<string, string>;

/* ================================================================
   Parsing + construction de l'index
   ================================================================ */
interface DocIndex {
    sections: DocSection[];
    pages: DocPage[];
    byPath: Map<string, DocPage>;
}

let _index: DocIndex | null = null;

function slugFromFilePath(filePath: string): { section: string; slug: string } {
    // ../docs/getting-started/quickstart.md → getting-started / quickstart
    const rel = filePath.replace('../docs/', '').replace(/\.md$/, '');
    const parts = rel.split('/');
    if (parts.length === 1) {
        // fichier à la racine (ex. index.md) — on le met dans une section "root"
        return { section: 'root', slug: parts[0] };
    }
    const section = parts[0];
    const slug = parts.slice(1).join('/');
    return { section, slug };
}

/**
 * Mini-parseur frontmatter YAML compatible navigateur (gray-matter dépend de
 * `Buffer` de Node et ne fonctionne pas côté client). Gère :
 *   ---
 *   clé: valeur
 *   clé: "chaîne avec : des deux-points"
 *   clé: [a, b, c]
 *   ---
 */
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
    const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
    if (!match) return { data: {}, content: raw };

    const yaml = match[1];
    const body = match[2];
    const data: Record<string, unknown> = {};

    for (const line of yaml.split(/\r?\n/)) {
        if (!line.trim() || line.trim().startsWith('#')) continue;
        const m = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(line);
        if (!m) continue;
        const key = m[1];
        let value: string = m[2].trim();

        // Tableaux en ligne : [a, b, "c d"]
        if (value.startsWith('[') && value.endsWith(']')) {
            const inner = value.slice(1, -1).trim();
            if (inner === '') {
                data[key] = [];
                continue;
            }
            data[key] = inner
                .split(',')
                .map((s) => stripQuotes(s.trim()))
                .filter((s) => s.length > 0);
            continue;
        }

        // Scalaires : quotes, booléens, nombres, null
        value = stripQuotes(value);
        if (value === 'true') data[key] = true;
        else if (value === 'false') data[key] = false;
        else if (value === 'null' || value === '~' || value === '') data[key] = null;
        else if (/^-?\d+(\.\d+)?$/.test(value)) data[key] = Number(value);
        else data[key] = value;
    }

    return { data, content: body };
}

function stripQuotes(s: string): string {
    if (
        (s.startsWith('"') && s.endsWith('"')) ||
        (s.startsWith("'") && s.endsWith("'"))
    ) {
        return s.slice(1, -1);
    }
    return s;
}

function parseOne(filePath: string, raw: string): DocPage | null {
    const { section, slug } = slugFromFilePath(filePath);
    const parsed = parseFrontmatter(raw);
    const fm = parsed.data as Partial<DocFrontmatter>;

    // Fallback sur le nom de fichier / dossier
    const title = fm.title ?? humanize(slug);
    const finalSection = fm.section ?? section;

    const frontmatter: DocFrontmatter = {
        title,
        description: fm.description,
        section: finalSection,
        order: fm.order ?? 999,
        status: fm.status ?? 'stable',
        updated: fm.updated,
        tags: fm.tags ?? [],
        hidden: fm.hidden ?? false,
    };

    const path = buildPath(finalSection, slug);

    return {
        slug,
        section: finalSection,
        path,
        content: parsed.content.trim(),
        frontmatter,
    };
}

function buildPath(section: string, slug: string): string {
    if (section === 'root') return '/docs';
    if (slug === 'index') return `/docs/${section}`;
    return `/docs/${section}/${slug}`;
}

function humanize(slug: string): string {
    return slug
        .split('/')
        .pop()!
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getDocIndex(): DocIndex {
    if (_index) return _index;

    const pages: DocPage[] = [];
    for (const [filePath, raw] of Object.entries(RAW_MODULES)) {
        const page = parseOne(filePath, raw);
        if (page && !page.frontmatter.hidden) pages.push(page);
    }

    // Regroupement par section
    const bySection = new Map<string, DocPage[]>();
    for (const p of pages) {
        if (!bySection.has(p.section)) bySection.set(p.section, []);
        bySection.get(p.section)!.push(p);
    }

    const sections: DocSection[] = [];
    for (const [id, list] of bySection.entries()) {
        const meta = SECTION_META[id];
        // Tri par order puis alpha
        list.sort((a, b) => {
            const ao = a.frontmatter.order ?? 999;
            const bo = b.frontmatter.order ?? 999;
            if (ao !== bo) return ao - bo;
            return a.frontmatter.title.localeCompare(b.frontmatter.title);
        });
        // Index page = celle qui a slug "index", sinon la 1re
        const idxPage = list.find((p) => p.slug === 'index') ?? list[0];
        sections.push({
            id,
            label: meta?.label ?? humanize(id),
            description: meta?.description,
            icon: meta?.icon ?? 'book',
            order: meta?.order ?? 999,
            pages: list,
            indexPath: idxPage?.path ?? `/docs/${id}`,
        });
    }

    sections.sort((a, b) => a.order - b.order);

    const byPath = new Map<string, DocPage>();
    for (const p of pages) byPath.set(p.path, p);

    _index = { sections, pages, byPath };
    return _index;
}

/**
 * Trouve la page qui correspond à un chemin `/docs/...`.
 * Gère :
 *   /docs                                   → page d'accueil
 *   /docs/<section>                         → index.md de la section
 *   /docs/<section>/<slug>
 *   /docs/<section>/<a>/<b>                 (slugs imbriqués)
 */
export function findDocByPath(pathname: string): DocPage | null {
    const idx = getDocIndex();
    const clean = pathname.replace(/\/$/, '') || '/docs';

    // Match exact
    const direct = idx.byPath.get(clean);
    if (direct) return direct;

    // Cas /docs/<section> → résoudre vers index de section
    const parts = clean.replace(/^\/docs\/?/, '').split('/').filter(Boolean);
    if (parts.length === 1) {
        const section = idx.sections.find((s) => s.id === parts[0]);
        if (section) return idx.byPath.get(section.indexPath) ?? null;
    }

    return null;
}

/* ================================================================
   Table des matières — extraction des h2/h3 du Markdown
   ================================================================ */
export interface TocEntry {
    id: string;
    level: 2 | 3;
    label: string;
}

export function extractToc(markdown: string): TocEntry[] {
    const lines = markdown.split('\n');
    const entries: TocEntry[] = [];
    let inCode = false;
    for (const line of lines) {
        if (line.startsWith('```')) {
            inCode = !inCode;
            continue;
        }
        if (inCode) continue;
        const m2 = /^##\s+(.+)$/.exec(line);
        const m3 = /^###\s+(.+)$/.exec(line);
        if (m2) {
            entries.push({ level: 2, label: m2[1].trim(), id: slugifyHeading(m2[1]) });
        } else if (m3) {
            entries.push({ level: 3, label: m3[1].trim(), id: slugifyHeading(m3[1]) });
        }
    }
    return entries;
}

/** Reproduit le comportement de rehype-slug (github-slugger simplifié). */
export function slugifyHeading(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
}

/* ================================================================
   Recherche plein-texte côté client
   ================================================================ */
export interface SearchHit {
    page: DocPage;
    snippet: string;
}

export function searchDocs(query: string, limit = 8): SearchHit[] {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const idx = getDocIndex();
    const scored: Array<{ page: DocPage; score: number; snippet: string }> = [];

    for (const page of idx.pages) {
        const haystack = [
            page.frontmatter.title,
            page.frontmatter.description ?? '',
            (page.frontmatter.tags ?? []).join(' '),
            page.content,
        ].join(' ').toLowerCase();

        if (!haystack.includes(q)) continue;

        // Score naïf : titre > description > tags > contenu
        let score = 0;
        if (page.frontmatter.title.toLowerCase().includes(q)) score += 10;
        if ((page.frontmatter.description ?? '').toLowerCase().includes(q)) score += 5;
        if ((page.frontmatter.tags ?? []).some((t) => t.toLowerCase().includes(q))) score += 3;
        score += 1;

        const snippet = buildSnippet(page.content, q);
        scored.push({ page, score, snippet });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map(({ page, snippet }) => ({ page, snippet }));
}

function buildSnippet(content: string, q: string): string {
    const lower = content.toLowerCase();
    const i = lower.indexOf(q);
    if (i < 0) return content.slice(0, 140);
    const start = Math.max(0, i - 60);
    const end = Math.min(content.length, i + q.length + 80);
    const slice = content.slice(start, end).replace(/\s+/g, ' ').trim();
    return (start > 0 ? '…' : '') + slice + (end < content.length ? '…' : '');
}
